import {Injectable} from "@angular/core";
import {Actions, createEffect, ofType} from '@ngrx/effects';
import * as websiteActions from '../actions/website.action';
import {filter, first, map, mergeMap, switchMap} from 'rxjs/operators';
import {WebsiteService} from '../../websites/website.service';
import * as userActions from '../actions/user.action';
import {Store} from '@ngrx/store';
import {DbStoreState} from '../reducers';
import aiBuilderDemoWebsiteJson from "../../../assets/json/ai-builder-demo-website.json";
import {combineLatest} from "rxjs";
import {Config} from "../../config/config";
import {TypeUtils} from "../../shared/utils/type-utils";
import {Utils} from "../../shared/utils/utils";
import {AddWebsite, UpdateWebsiteData, UpdateWebsiteDataSuccess} from "../actions/website.action";
import {DbResponse} from "../../shared/interceptors/db-response";
import * as activityActions from "../actions/activities.action";
import * as fromStore from "../../store";
import * as fromUserSelectors from "../selectors/user.selector";
import {AddActivity} from "../actions/activities.action";
import {ActivityAction, ActivityStatus} from "../../activities/activity";
import {Website} from "../../websites/website/website";


@Injectable()
export class WebsiteEffects {
  loadUserSuccess$ = createEffect(() => this._actions$.pipe(
    ofType(userActions.LOAD_USER_SUCCESS),
    filter((loadUserSuccess: any) => !!loadUserSuccess.payload),
    map(() => new websiteActions.LoadWebsites())
  ));
  logOutUser$ = createEffect(() => this._actions$.pipe(
    ofType(userActions.USER_LOG_OUT),
    map(() => new websiteActions.ClearWebsites())
  ));
  private _aiBuilderDemoWebsite = aiBuilderDemoWebsiteJson;

  constructor(
    private _store: Store<DbStoreState>,
    private _actions$: Actions,
    private _websiteService: WebsiteService
  ) {
  }

  loadWebsites$ = createEffect(() => this._actions$.pipe(
    ofType(websiteActions.LOAD_WEBSITES),
    switchMap(() => combineLatest([
      this._store.select(fromUserSelectors.getUserSubscription),
      this._store.select(fromUserSelectors.getUser)
    ]).pipe(
      filter(([userSubscription, user]) => !!userSubscription && !!user),
      first()
    )),
    switchMap(([userSubscription, user]) => {
      const demoUser = Utils.isDemoUser(userSubscription, user);
      if (demoUser) {
        return this._websiteService.getAiBuilderDemoWebsite().pipe(
          filter((response: DbResponse) => !!response),
          map((response: DbResponse) => {
            if (response.success && response.data && response.data.page_url) {
              const aiBuilderDemoWebsiteData = this._aiBuilderDemoWebsite;
              aiBuilderDemoWebsiteData['id'] = Config.demoDomainId;
              aiBuilderDemoWebsiteData['site_url'] = response.data.page_url;
              aiBuilderDemoWebsiteData.site_title = response.data.site_title;
              aiBuilderDemoWebsiteData['ai_builder_demo'] = true;
              aiBuilderDemoWebsiteData['template_type'] = response.data.template_type;
              aiBuilderDemoWebsiteData['styles'] = response.data.styles;
              return new websiteActions.LoadWebsitesSuccess(Utils.arrayToEntities([aiBuilderDemoWebsiteData], 'id'));
            }
            return new websiteActions.LoadWebsitesFail(response.message);
          }));
      } else {
        const observables = [this._websiteService.getWebsites()];
        const fromDemo = !!(user && user.signUpFor && user.signUpFor.LastChosenOption && user.signUpFor.LastChosenOption.answer
          && ['ai-demo-section', 'ai-demo-section-after-signup', 'ai-demo-template', 'ai-demo-template-after-signup'].includes(user.signUpFor.LastChosenOption.answer));
        /*add get page link for base and non ai-demo clients because we have email campaign where users will go through demo flow after expire/abandoned base*/
        if (userSubscription.freeBaseClient || userSubscription.trialClient || (user?.platformClient && !user?.websiteCreated)) {
          observables.push(this._websiteService.getAiBuilderDemoWebsite());
        }

        return combineLatest(observables).pipe(
          first(([websites, aiBuilderDemo]) => !!websites),
          map(([websites, aiBuilderDemo]) => {
            if (websites.success) {
              websites.data.map((website) => {
                if (website.domain_hash && website.logged_out && !website.is_stopped) {
                  this._websiteService.autoLogin(website.id);
                }
              });

              let allWebsites = websites.data;
              if (TypeUtils.isDefined(aiBuilderDemo)) {
                if (aiBuilderDemo && aiBuilderDemo?.success && aiBuilderDemo?.data && aiBuilderDemo?.data.page_url) {
                  const aiBuilderDemoWebsiteData = this._aiBuilderDemoWebsite;
                  aiBuilderDemoWebsiteData['id'] = Config.demoDomainId;
                  aiBuilderDemoWebsiteData["site_url"] = aiBuilderDemo.data.page_url;
                  aiBuilderDemoWebsiteData.site_title = aiBuilderDemo.data.site_title;
                  aiBuilderDemoWebsiteData['ai_builder_demo'] = true;
                  aiBuilderDemoWebsiteData['template_type'] = aiBuilderDemo.data.template_type;
                  allWebsites = [...websites.data, ...[aiBuilderDemoWebsiteData]];
                }
              }
              return new websiteActions.LoadWebsitesSuccess(Utils.arrayToEntities(allWebsites, 'id'));
            }
            return new websiteActions.LoadWebsitesFail(websites.message);
          })
        );
      }
    })
  ));

  updateWebsiteData$ = createEffect(() => this._actions$.pipe(
    ofType(websiteActions.UPDATE_WEBSITE_DATA),
    switchMap((action: UpdateWebsiteData) => {
      return this._websiteService.getWebsite(action.payload)
        .pipe(
          filter((response: DbResponse) => !!response),
          map((response: DbResponse) => {
            if (response.success) {
              return new websiteActions.UpdateWebsiteDataSuccess(response.data);
            }
            return new websiteActions.UpdateWebsiteDataFail(response.message);
          })
        );
    })
  ));

  addWebsiteActivity$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => action.payload.action === ActivityAction.ADD_WEBSITE),
    mergeMap((action: AddActivity) => {
      // Assuming you want to conditionally call actions based on `action.payload.website` existence
      const actions = [];

      if (action && action.payload) {

        if (action.payload.websiteId) { // Check if `websiteId` exists before adding it
          new websiteActions.UpdateWebsiteData(action.payload.websiteId);
        }

        if (action.payload.meta) { // Check if `meta` exists before adding it
          actions.push(new websiteActions.AddWebsite(action.payload.meta));
        }
      }

      return actions;
    })
  ));

  generateWebsiteScreenShotActivity$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => action.payload.action === ActivityAction.GENERATE_WEBSITE_SCREENSHOT),
    map((action: AddActivity) => new websiteActions.UpdateWebsiteScreenShot({
      websiteId: action.payload.websiteId,
      screenshot: action.payload.meta.streamp
    }))
  ));

  migrateWebsiteActivity$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => action.payload.action === ActivityAction.MIGRATE_WEBSITE),
    filter((action: AddActivity): boolean => action.payload.status === ActivityStatus.DONE),
    map((action: AddActivity) => new websiteActions.UpdateWebsiteData(action.payload.websiteId))
  ));

  performanceGoogleScoreWebsiteActivity$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => action.payload.action === ActivityAction.PERFORMANCE_GOOGLE_SCORE),
    filter((action: AddActivity): boolean => action.payload.status === ActivityStatus.DONE),
    map((action: AddActivity) => new websiteActions.UpdateWebsiteGoogleScore({
      websiteId: action.payload.websiteId,
      score: action.payload.meta || null
    }))
  ));

  addWebsite$ = createEffect(() => this._actions$.pipe(
    ofType(websiteActions.ADD_WEBSITE),
    map((action: AddWebsite) => new websiteActions.UpdateWebsiteSubData(action.payload))
  ));

  createWebsite$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => [ActivityAction.CREATE_WEBSITE, ActivityAction.CREATE_STAGING_WEBSITE].includes(<any>action.payload.action) && action.payload.status === ActivityStatus.DONE),
    map((action: AddActivity) => new websiteActions.UpdateWebsiteData(action.payload.meta.id))
  ));

  updateWebsiteDataSuccess$ = createEffect(() => this._actions$.pipe(
    ofType(websiteActions.UPDATE_WEBSITE_DATA_SUCCESS),
    switchMap((action: UpdateWebsiteDataSuccess) => this._store.select(fromStore.getWebsite(action.payload.id))),
    filter((website: Website): boolean => !!website),
    map((website: Website) => new websiteActions.UpdateWebsiteSubData(website))
  ));

  deactivateWebsiteActivity$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => action.payload.action === ActivityAction.DEACTIVATE_WEBSITE),
    mergeMap((action: AddActivity) => [
      new websiteActions.UpdateWebsiteData(action.payload.websiteId),
      new websiteActions.DeactivateWebsite(action.payload.meta)
    ])
  ));

  removeWebsiteActivity$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => action.payload.action === ActivityAction.REMOVE_WEBSITE),
    mergeMap((action: AddActivity) => [
      new websiteActions.RemoveWebsite(action.payload.websiteId)
    ])
  ));

  updateWebsiteWordpressVersionActivity$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => action.payload.action === ActivityAction.UPDATE_WEBSITE_WORDPRESS_VERSION),
    filter((action: AddActivity): boolean => action.payload.status === ActivityStatus.DONE),
    map((action: AddActivity) => new websiteActions.UpdateWebsiteWordpressVersion({
      websiteId: action.payload.websiteId,
      wordpressVersion: action.payload.meta.wp_version
    }))
  ));
}
